/* eslint-disable complexity */
'use client';

import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MinusIcon, SearchIcon } from 'lucide-react';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { UseQueryResult } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { Input, Checkbox } from '@/slw/shared/ui/form';
import { cn } from '@/slw/shared/lib/utils';
import { FilterSection } from '@/slw/features/search-results/filter-section.ui';
import { FilterCheckboxGroup } from '@/slw/features/search-results/filter-checkbox-group.ui';
import { Slider } from '@/slw/shared/ui/slider';
import { AllModel, AllState, ListingsFilter } from '@/slw/entities/search-results/search-results.type';
import { HomeFilterType } from '@/slw/entities/home/home.model';
import { PageResponse } from '@/slw/shared/types/api.type';
import { formatNumber } from '@/slw/shared/lib/format-number';
import { Checkbox as BaseCheckbox } from '@/slw/shared/ui/checkbox';
import { formatNumberInput, formatSaveNumber } from '@/slw/shared/ui/form/input';

const currentYear = Number(dayjs().format('YYYY'));

const filterSchema = z
  .object({
    keyword: z.string().max(50).optional(),
    filterByTag: z.array(z.string()).optional(),
    premiumSpotlight: z.boolean().optional(),
    orBestOffer: z.boolean().optional(),
    fromYear: z.string().optional(),
    toYear: z.string().optional(),
    fromPrice: z.string().optional(),
    toPrice: z.string().optional(),
    fromOdometer: z.string().optional(),
    toOdometer: z.string().optional(),
    country: z.array(z.string()).optional(),
    state: z.array(z.string()).optional(),
    make: z.array(z.string()).optional(),
    model: z.array(z.string()).optional(),
    transmission: z.array(z.string()).optional(),
    engineHistory: z.array(z.string()).optional(),
    engineCondition: z.array(z.string()).optional(),
    seatMaterial: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.keyword) {
        return data.keyword.length <= 50;
      }

      return true;
    },
    {
      message: 'Keyword must be less than or equal to 50 characters',
      path: ['toYear'],
    },
  )
  .refine(
    (data) => {
      if (data.fromYear !== undefined && data.toYear !== undefined && data.fromYear !== '' && data.toYear !== '') {
        return formatSaveNumber(data.fromYear) <= formatSaveNumber(data.toYear);
      }

      return true;
    },
    {
      message: 'From year must be less than or equal to To year',
      path: ['toYear'],
    },
  )
  .refine(
    (data) => {
      if (data.fromPrice !== undefined && data.toPrice !== undefined && data.fromPrice !== '' && data.toPrice !== '') {
        return formatSaveNumber(data.fromPrice) <= formatSaveNumber(data.toPrice);
      }

      return true;
    },
    {
      message: 'Lowest price must be less than or equal to Highest price',
      path: ['toPrice'],
    },
  )
  .refine(
    (data) => {
      if (
        data.fromOdometer !== undefined &&
        data.toOdometer !== undefined &&
        data.fromOdometer !== '' &&
        data.toOdometer !== '' &&
        data.fromOdometer !== null &&
        data.toOdometer !== null
      ) {
        return formatSaveNumber(data.fromOdometer) <= formatSaveNumber(data.toOdometer);
      }

      return true;
    },
    {
      message: 'Lowest mileage must be less than or equal to Highest mileage',
      path: ['toOdometer'],
    },
  )
  .refine(
    (data) => {
      if (data.fromYear !== '' && data.fromYear !== undefined && data.fromYear !== null) {
        return formatSaveNumber(data.fromYear) >= 1000 && formatSaveNumber(data.fromYear) <= currentYear;
      }

      return true;
    },
    {
      message: 'From year must be greater than 1000 and less than or equal to ' + currentYear,
      path: ['fromYear'],
    },
  )
  .refine(
    (data) => {
      if (data.toYear !== '' && data.toYear !== undefined && data.toYear !== null) {
        return formatSaveNumber(data.toYear) >= 1000 && formatSaveNumber(data.toYear) <= currentYear;
      }

      return true;
    },
    {
      message: 'To year must be greater than 1000 and less than or equal to ' + currentYear,
      path: ['toYear'],
    },
  )
  .refine(
    (data) => {
      if (data.fromPrice !== '' && data.fromPrice !== undefined && data.fromPrice !== null) {
        return formatSaveNumber(data.fromPrice) >= 0 && formatSaveNumber(data.fromPrice) <= 3000000;
      }

      return true;
    },
    {
      message: 'Lowest price must be greater than 0 and less than or equal to 3,000,000',
      path: ['fromPrice'],
    },
  )
  .refine(
    (data) => {
      if (data.toPrice !== '' && data.toPrice !== undefined && data.toPrice !== null) {
        return formatSaveNumber(data.toPrice) >= 0 && formatSaveNumber(data.toPrice) <= 3000000;
      }

      return true;
    },
    {
      message: 'Highest price must be greater than 0 and less than or equal to 3,000,000',
      path: ['toPrice'],
    },
  )
  .refine(
    (data) => {
      if (data.fromOdometer !== '' && data.fromOdometer !== undefined && data.fromOdometer !== null) {
        return formatSaveNumber(data.fromOdometer) >= 0 && formatSaveNumber(data.fromOdometer) <= 999999;
      }

      return true;
    },
    {
      message: 'Lowest mileage must be greater than 0 and less than or equal to 999,999',
      path: ['fromOdometer'],
    },
  )
  .refine(
    (data) => {
      if (data.toOdometer !== '' && data.toOdometer !== undefined && data.toOdometer !== null) {
        return formatSaveNumber(data.toOdometer) >= 0 && formatSaveNumber(data.toOdometer) <= 999999;
      }

      return true;
    },
    {
      message: 'Highest mileage must be greater than 0 and less than or equal to 999,999',
      path: ['toOdometer'],
    },
  );

type FilterFormValues = z.infer<typeof filterSchema>;

interface FilterSidebarTabProps {
  allModel: UseQueryResult<PageResponse<AllModel[]>, any>;
  allState: UseQueryResult<PageResponse<AllState[]>, any>;
  filter: ListingsFilter;
  homeFilter: HomeFilterType;
  storeSetValue: RefObject<any>;
  storeResetForm: RefObject<any>;
  // setFilter: (f: any) => void;
  replaceUrl: (url: string, values: ListingsFilter) => void;
}

function FilterSidebarTabComponent({
  allModel,
  allState,
  filter,
  homeFilter,
  storeSetValue,
  storeResetForm,
  // setFilter,
  replaceUrl,
}: FilterSidebarTabProps) {
  const [stateOptions, setStateOptions] = useState<AllState[]>([]);
  const [modelOptions, setModelOptions] = useState<AllModel[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUpdateStateModelRef = useRef<boolean>(false);
  const defaultValues = useMemo(
    () => ({
      keyword: filter.keyword || '',
      filterByTag: filter.filterByTag || [],
      premiumSpotlight: filter.premiumSpotlight ?? false,
      orBestOffer: filter.orBestOffer ?? false,
      fromYear: filter.fromYear ?? undefined,
      toYear: filter.toYear ?? undefined,
      fromPrice: filter.fromPrice ?? undefined,
      toPrice: filter.toPrice ?? undefined,
      fromOdometer: filter.fromOdometer ?? undefined,
      toOdometer: filter.toOdometer ?? undefined,
      country: filter.country || [],
      state: filter.state || [],
      make: filter.make || [],
      model: filter.model || [],
      transmission: filter.transmission || [],
      engineHistory: filter.engineHistory || [],
      engineCondition: filter.engineCondition || [],
      seatMaterial: filter.seatMaterial || [],
    }),
    [filter],
  );

  const methods = useForm<FilterFormValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: defaultValues as FilterFormValues,
  });

  const { watch, control, formState, setValue, reset } = methods;
  const values = watch();

  const handleChangeCountry = useCallback(
    (value: string[]) => {
      if (allState.data?.data && allState.data?.data.length) {
        if (value?.length) {
          setStateOptions(allState.data.data.filter((st: AllState) => value.includes(st.countrySlug)));
        } else {
          setStateOptions(allState.data.data);
        }
      } else {
        setStateOptions(allState.data?.data || []);
      }
    },
    [allState?.data?.data],
  );

  const handleChangeMake = useCallback(
    (value: string[]) => {
      if (allModel.data?.data && allModel.data?.data.length) {
        if (value.length) {
          setModelOptions(allModel.data.data.filter((st: AllModel) => value.includes(st.makeSlug)));
        } else {
          setModelOptions(allModel.data.data);
        }
      } else {
        setModelOptions(allModel.data?.data || []);
      }
    },
    [allModel?.data?.data],
  );


  useEffect(() => {
    storeSetValue.current = setValue;
    storeResetForm.current = reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  useEffect(() => {
    if (!allState?.data?.data?.length || isUpdateStateModelRef.current) return;

    requestAnimationFrame(() => {
      isUpdateStateModelRef.current = true;
      handleChangeCountry(defaultValues.country);
    });
  }, [defaultValues.country, handleChangeCountry, allState?.data?.data]);

  useEffect(() => {
    if (!allModel?.data?.data?.length || isUpdateStateModelRef.current) return;

    requestAnimationFrame(() => {
      isUpdateStateModelRef.current = true;
      handleChangeMake(defaultValues.make);
    });
  }, [defaultValues.make, handleChangeMake, allModel?.data?.data]);

  useEffect(() => {
    if (!isEqual(values.filterByTag, filter.filterByTag)) {
      setValue('filterByTag', filter.filterByTag || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.filterByTag]);

  useEffect(() => {
    const handleSearch = (values: FilterFormValues) => {
      if (JSON.stringify(defaultValues) === JSON.stringify(values)) return;

      // setFilter((filter: ListingsFilter) => ({ ...filter, ...values, pageIndex: 1 }));

      const year = Number(values.fromYear) ? Number(values.fromYear) : 'all-year';

      // if (values.make && values.make.length === 1) {
      //   if (values.model && values.model.length === 1) {
      //     replaceUrl(`/search-results/${year}/${values.make[0]}/${values.model[0]}`, values);
      //   } else {
      //     replaceUrl(`/search-results/${year}/${values.make[0]}`, values);
      //   }
      // } else {
      replaceUrl(`/search-results/${year}`, values);
      // }
    };

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      methods.handleSubmit(handleSearch)();
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.keyword,
    values.filterByTag,
    values.premiumSpotlight,
    values.orBestOffer,
    values.fromYear,
    values.toYear,
    values.fromPrice,
    values.toPrice,
    values.fromOdometer,
    values.toOdometer,
    values.country,
    values.state,
    values.make,
    values.model,
    values.transmission,
    values.engineHistory,
    values.engineCondition,
    values.seatMaterial,
  ]);

  return (
    <FormProvider {...methods}>
      <form>
        <aside
          className='w-full bg-[#F8F8F7] border border-[#BC9B651A]
       rounded-md  px-4 py-8 min-h-[600px] sticky top-8 flex flex-col gap-8'
        >
          {/* Search */}
          <div className='mb-4'>
            <h3 className='text-lg font-semibold mb-2.5'>Describe your ideal classic</h3>
            <div className='relative'>
              <Input
                direction='column'
                name='keyword'
                maxLength={50}
                withError={false}
                placeholder='Search'
                className='rounded-sm px-4 py-3 pl-10'
              />
              <span className='absolute left-4 top-1/2 -translate-y-[calc(50%)] text-[#B0A89F]'>
                <SearchIcon className='w-5 h-5' />
              </span>
            </div>
            {formState.errors.keyword && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.keyword.message}</div>
            )}
          </div>
          {/* Tags */}
          <div className='flex flex-col gap-2.5'>
            <h2 className='text-lg font-semibold'>Filter by Tags</h2>
            <Controller
              name='filterByTag'
              control={control}
              render={({ field }) => (
                <div className='flex flex-col gap-2'>
                  {(homeFilter.vehicleEra || []).map((tag) => (
                    <div
                      key={tag.code}
                      className={cn(
                        `flex justify-between w-full text-left rounded-md py-4 px-4 text-sm
                        leading-[150%] font-medium bg-white transition`,
                        (field.value || []).includes(tag.code) ? 'bg-[#E5E3DF]' : '',
                      )}
                    >
                      <span className='flex items-center gap-2'>
                        <BaseCheckbox
                          onCheckedChange={() => {
                            if ((field.value || []).includes(tag.code)) {
                              field.onChange((field.value || []).filter((v) => v !== tag.code));
                            } else {
                              field.onChange([...(field.value || []), tag.code]);
                            }
                          }}
                          className='w-6 h-6'
                          checked={(field.value || []).includes(tag.code)}
                        />{' '}
                        {tag.name}
                      </span>
                      <span>{tag.count ? formatNumber(tag.count) : tag.count}</span>
                    </div>
                  ))}
                </div>
              )}
            />
          </div>
          {/* Premium/OBO checkboxes */}
          <div className='flex flex-col gap-4'>
            <div className='flex items-center justify-between'>
              <Checkbox
                name='premiumSpotlight'
                label='Premium Spotlight'
                checkboxPos='right'
                className='w-6 h-6 rounded-md'
              />
              <span className=' text-sm'>
                {homeFilter.premiumSpotlight?.count ? formatNumber(homeFilter.premiumSpotlight.count) : 0}
              </span>
            </div>
            <div className='flex items-center justify-between my-4'>
              <Checkbox
                name='orBestOffer'
                label='Or best offer (OBO)'
                checkboxPos='right'
                className='w-6 h-6 rounded-md'
              />
              <span className=' text-sm'>
                {homeFilter.orBestOffer?.count ? formatNumber(homeFilter.orBestOffer.count) : 0}
              </span>
            </div>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          {/* Year Range */}
          <div className='flex flex-col gap-4'>
            <div className='text-lg font-semibold'>Year</div>
            <div className='flex gap-2 items-center'>
              <Input
                name='fromYear'
                placeholder='From year'
                containerClass='w-1/2'
                className={formState.errors.fromYear ? 'border-red-500' : ''}
                withError={false}
                onlyNumber
                min={1000}
                max={currentYear}
              />
              <MinusIcon size={16} />
              <Input
                name='toYear'
                placeholder='To year'
                containerClass='w-1/2'
                className={formState.errors.toYear ? 'border-red-500' : ''}
                withError={false}
                onlyNumber
                min={1000}
                max={currentYear}
              />
            </div>
            <Slider
              value={[formatSaveNumber(values.fromYear) || 1000, formatSaveNumber(values.toYear) || currentYear]}
              min={1000}
              max={currentYear}
              step={1}
              className='slider-fancy'
              onValueChange={(values) => {
                methods.setValue('fromYear', `${values[0]}`, { shouldValidate: true });
                methods.setValue('toYear', `${values[1]}`, { shouldValidate: true });
              }}
            />
            {formState.errors.fromYear && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.fromYear.message}</div>
            )}
            {formState.errors.toYear && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.toYear.message}</div>
            )}
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          {/* Price Range */}
          <div className='flex flex-col gap-4'>
            <div className='text-lg font-semibold mb-2'>Price</div>
            <div className='flex gap-2 items-center'>
              <Input
                name='fromPrice'
                placeholder='Lowest price'
                containerClass='w-1/2'
                className={formState.errors.fromPrice ? 'border-red-500' : ''}
                withError={false}
                // type='number'
                min={0}
                max={3000000}
                onlyNumberFormat
              />
              <MinusIcon size={16} />
              <Input
                name='toPrice'
                placeholder='Highest price'
                containerClass='w-1/2'
                className={formState.errors.toPrice ? 'border-red-500' : ''}
                withError={false}
                // type='number'
                min={0}
                max={3000000}
                onlyNumberFormat
              />
            </div>
            <Slider
              value={[formatSaveNumber(values.fromPrice) || 0, formatSaveNumber(values.toPrice) || 3000000]}
              min={0}
              max={3000000}
              step={2000}
              className='slider-fancy'
              onValueChange={(values) => {
                methods.setValue('fromPrice', `${formatNumberInput(values[0])}`, { shouldValidate: true });
                methods.setValue('toPrice', `${formatNumberInput(values[1])}`, { shouldValidate: true });
              }}
            />
            {formState.errors.fromPrice && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.fromPrice.message}</div>
            )}
            {formState.errors.toPrice && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.toPrice.message}</div>
            )}
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          {/* Mileage Range */}
          <div className='flex flex-col gap-4'>
            <div className='text-lg font-semibold mb-2'>Mileage</div>
            <div className='flex gap-2 items-center'>
              <Input
                name='fromOdometer'
                placeholder='Lowest mileage'
                containerClass='w-1/2'
                className={formState.errors.fromOdometer ? 'border-red-500' : ''}
                withError={false}
                // type='number'
                min={0}
                max={999999}
                onlyNumberFormat
              />
              <MinusIcon size={16} />
              <Input
                name='toOdometer'
                placeholder='Highest mileage'
                containerClass='w-1/2'
                className={formState.errors.toOdometer ? 'border-red-500' : ''}
                withError={false}
                // type='number'
                min={0}
                max={999999}
                onlyNumberFormat
              />
            </div>

            <Slider
              name='mileage'
              value={[formatSaveNumber(values.fromOdometer) || 0, formatSaveNumber(values.toOdometer) || 0]}
              min={0}
              max={999999}
              step={1000}
              className='slider-fancy'
              onValueChange={(values) => {
                methods.setValue('fromOdometer', `${formatNumberInput(values[0])}`, { shouldValidate: true });
                methods.setValue('toOdometer', `${formatNumberInput(values[1])}`, { shouldValidate: true });
              }}
            />
            {formState.errors.fromOdometer && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.fromOdometer.message}</div>
            )}
            {formState.errors.toOdometer && (
              <div className='text-xs text-red-500 mt-1'>{formState.errors.toOdometer.message}</div>
            )}
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          {/* Advanced Filter Groups */}
          <div className='flex flex-col gap-4'>
            <FilterSection label='Country'>
              <Controller
                name='country'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Country'
                    options={(homeFilter.country || []).map((item) => ({
                      label: item.country,
                      value: item.countrySlug,
                      count: item.count,
                    }))}
                    searchable
                    value={field.value || []}
                    onChange={(value: string[]) => {
                      setValue('state', []);
                      field.onChange(value);

                      handleChangeCountry(value);
                    }}
                    name='country'
                    toggleableSelectAll
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='State'>
              <Controller
                name='state'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='State'
                    options={(stateOptions || []).map((item) => ({
                      label: item.state,
                      value: item.stateSlug,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                    searchable
                    name='state'
                    toggleableSelectAll
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Make' defaultOpen={true}>
              <Controller
                name='make'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Make'
                    options={(homeFilter.make || []).map((item) => ({
                      label: item.make,
                      value: item.makeSlug,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    // onChange={field.onChange}
                    onChange={(value: string[]) => {
                      setValue('model', []);
                      field.onChange(value);

                      handleChangeMake(value);
                    }}
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Model' defaultOpen={true}>
              <Controller
                name='model'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Model'
                    options={(modelOptions || []).map((item) => ({
                      label: item.model,
                      value: item.modelSlug,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Transmission' defaultOpen={false}>
              <Controller
                name='transmission'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Transmission'
                    options={(homeFilter.transmission || []).map((item) => ({
                      label: item.name,
                      value: item.code,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Engine History' defaultOpen={false}>
              <Controller
                name='engineHistory'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Engine History'
                    options={(homeFilter.engineHistory || []).map((item) => ({
                      label: item.name,
                      value: item.code,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Engine Condition' defaultOpen={false}>
              <Controller
                name='engineCondition'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Engine Condition'
                    options={(homeFilter.engineCondition || []).map((item) => ({
                      label: item.name,
                      value: item.code,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </FilterSection>
            <Separator className='w-full h-[1px] bg-[#EBE9E5]' />
          </div>
          <div className='flex flex-col gap-4'>
            <FilterSection label='Seat Material' defaultOpen={false}>
              <Controller
                name='seatMaterial'
                control={control}
                render={({ field }) => (
                  <FilterCheckboxGroup
                    label='Seat Material'
                    options={(homeFilter.seatMaterial || []).map((item) => ({
                      label: item.name,
                      value: item.code,
                      count: item.count,
                    }))}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </FilterSection>
          </div>
        </aside>
      </form>
    </FormProvider>
  );
}

export const FilterSidebarTab = React.memo(FilterSidebarTabComponent);
